import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Tag,
  Button,
  Space,
  Card,
  Typography,
  Popconfirm,
  Tooltip,
  Select,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CalendarOutlined,
  UserOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { leaveService } from '../../services/leaveService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import notify from '../../utils/notify';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import RejectionModal from './RejectionModal';
import formatDateRo from '../../utils/formatDateRo';
import ViewModeSelect from '../common/ViewModeSelect';
import useResponsiveTableView from '../../hooks/useResponsiveTableView';

const { Text } = Typography;

const VALID_STATUS_FILTERS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
const DEFAULT_STATUS_FILTER = 'PENDING';
const STATUS_FILTER_PARAM = 'status';
const EMPLOYEE_FILTER_ALL = 'ALL';
const REJECTED_STATUS = 'REJECTED';
const APPROVED_STATUS = 'APPROVED';
// Below this container width the table can't be shown in full → cards. The leave list
// sits inside nested cards (≈932px content at a 1280px viewport), so the threshold is
// tuned to keep the table on a standard desktop and fall back to cards on tablets/phones.
const MIN_TABLE_WIDTH = 850;

const LeaveRequestList = ({ onEdit }) => {
  const [searchParams] = useSearchParams();
  const requestedStatus = (searchParams.get(STATUS_FILTER_PARAM) || '').toUpperCase();
  const initialStatusFilter = VALID_STATUS_FILTERS.includes(requestedStatus)
    ? requestedStatus
    : DEFAULT_STATUS_FILTER;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [employeeFilter, setEmployeeFilter] = useState(EMPLOYEE_FILTER_ALL);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [manualView, setManualView] = useState('auto');
  const { user, canApproveLeaveRequests } = useAuth();
  const { t } = useTranslation();
  const { containerRef, view } = useResponsiveTableView({ minTableWidth: MIN_TABLE_WIDTH, manualView });
  const canApprove =
    canApproveLeaveRequests?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR'].includes(role));

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async () => {
    try {
      let data;
      if (canApprove) {
        data = await leaveService.getAllRequests();
      } else {
        if (user?.employeeId === null || user?.employeeId === undefined) {
          setRequests([]);
          notify.error(t('leave.fetchError'));
          return;
        }

        // Fetch user's own requests using their employee ID
        data = await leaveService.getByEmployee(user.employeeId);
      }
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      notify.error(t('leave.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (requestId, employeeName) => {
    try {
      await leaveService.downloadPdf(requestId, employeeName);
      notify.success(t('leave.downloadPdfSuccess'));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notify.error(t('leave.downloadPdfError'));
    }
  };

  const handleApproveReject = async (requestId, status) => {
    try {
      await leaveService.approveOrReject(requestId, { status });
      const successKey = status === 'APPROVED' ? 'leave.approveSuccess' : 'leave.rejectSuccess';
      notify.success(t(successKey));
      fetchRequests();
    } catch (error) {
      console.error('Error updating leave request:', error);
      notify.error(getApiErrorMessage(error, 'leave.updateError', t));
    }
  };

  const handleRejectSubmit = async (reason) => {
    if (!rejectTarget) return;
    try {
      await leaveService.approveOrReject(rejectTarget.id, {
        status: REJECTED_STATUS,
        rejectionReason: reason,
      });
      notify.success(t('leave.rejectSuccess'));
      setRejectTarget(null);
      fetchRequests();
      return true;
    } catch (error) {
      console.error('Error updating leave request:', error);
      notify.error(getApiErrorMessage(error, 'leave.updateError', t));
      return false;
    }
  };

  const closeRejectModal = () => {
    setRejectTarget(null);
  };

  // Secondary lines shown under the status tag: who actioned the request and, for rejections,
  // the reason the approver gave. Returns an array so it stacks cleanly inside an AntD Space.
  const renderApprovalMeta = (record) => {
    const lines = [];
    if (record.status === APPROVED_STATUS && record.approvedByName) {
      lines.push(
        <Text key="actor" type="secondary" style={{ fontSize: 12 }} data-testid="approved-by">
          {t('leave.approvedBy', { name: record.approvedByName })}
        </Text>
      );
    }
    if (record.status === REJECTED_STATUS) {
      if (record.approvedByName) {
        lines.push(
          <Text key="actor" type="secondary" style={{ fontSize: 12 }} data-testid="rejected-by">
            {t('leave.rejectedBy', { name: record.approvedByName })}
          </Text>
        );
      }
      if (record.rejectionReason) {
        lines.push(
          <Text key="reason" type="secondary" style={{ fontSize: 12 }} data-testid="rejection-reason">
            {t('leave.rejectionReason')}: {record.rejectionReason}
          </Text>
        );
      }
    }
    return lines;
  };

  const handleDelete = async (requestId) => {
    try {
      await leaveService.deleteRequest(requestId);
      notify.success(t('leave.deleteSuccess'));
      fetchRequests();
    } catch (error) {
      console.error('Error deleting leave request:', error);
      notify.error(getApiErrorMessage(error, 'leave.deleteError', t));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case 'VACATION':
        return 'blue';
      case 'SICK':
        return 'red';
      case 'MATERNITY':
      case 'PATERNITY':
        return 'pink';
      case 'BEREAVEMENT':
        return 'black';
      case 'MARRIAGE':
        return 'purple';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: t('leave.columns.employee'),
      key: 'employee',
      sorter: (a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''),
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <Text strong>{record.employeeName}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.departmentName}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: t('leave.columns.leaveType'),
      dataIndex: 'leaveType',
      key: 'leaveType',
      sorter: (a, b) => (a.leaveType || '').localeCompare(b.leaveType || ''),
      render: (type) => (
        <Tag color={getLeaveTypeColor(type)}>
          {t(`leave.form.types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('leave.columns.duration'),
      key: 'duration',
      sorter: (a, b) => new Date(a.startDate) - new Date(b.startDate),
      render: (_, record) => (
        <Space>
          <CalendarOutlined />
          <Text>
            {formatDateRo(record.startDate)} - {formatDateRo(record.endDate)}
          </Text>
        </Space>
      ),
    },
    {
      title: t('leave.columns.days'),
      dataIndex: 'workingDays',
      key: 'workingDays',
      onCell: () => ({ 'data-testid': 'working-days' }),
      sorter: (a, b) => a.workingDays - b.workingDays,
      render: (days) => {
        const key = days === 1 ? 'leave.daysCount_one' : 'leave.daysCount_other';
        return t(key, { count: days });
      },
    },
    {
      title: t('leave.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Space direction="vertical" size={2} align="start">
          <Tag color={getStatusColor(status)}>
            {t(`leave.status.${status}`)}
          </Tag>
          {renderApprovalMeta(record)}
        </Space>
      ),
    },
    {
      title: t('leave.columns.reason'),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      sorter: (a, b) => (a.reason || '').localeCompare(b.reason || ''),
      render: (reason) => reason || '-',
    },
    {
      title: t('leave.columns.actions'),
      key: 'actions',
      fixed: view === 'table' ? 'right' : undefined,
      render: (_, record) => {
        // Employees see edit/delete only on their own pending requests.
        if (!canApprove) {
          if (record.status !== 'PENDING') return null;
          return (
            <Space>
              <Tooltip title={t('common.edit')}>
                <Button
                  size="small"
                  className="touch-button"
                  icon={<EditOutlined />}
                  onClick={() => onEdit && onEdit(record)}
                  data-testid="edit-leave"
                />
              </Tooltip>
              <Popconfirm
                title={t('leave.deleteRequest')}
                description={t('leave.deleteConfirm')}
                onConfirm={() => handleDelete(record.id)}
                okText={t('common.yes')}
                cancelText={t('common.no')}
              >
                <Button
                  size="small"
                  className="touch-button"
                  icon={<DeleteOutlined />}
                  data-testid="delete-leave"
                />
              </Popconfirm>
            </Space>
          );
        }

        // Admin/HR: PDF + approve/reject (PENDING only) + edit/delete (anything but REJECTED).
        return (
          <Space>
            <Tooltip title={t('leave.downloadPdf')}>
              <Button
                size="small"
                className="touch-button"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadPdf(record.id, record.employeeName)}
                data-testid="download-pdf"
              />
            </Tooltip>
            {record.status === 'PENDING' && (
              <>
                <Tooltip title={t('leave.approveBtn')}>
                  <Popconfirm
                    title={t('leave.approveRequest')}
                    description={t('leave.approveConfirm')}
                    onConfirm={() => handleApproveReject(record.id, 'APPROVED')}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                  >
                    <Button
                      type="primary"
                      size="small"
                      className="touch-button"
                      icon={<CheckOutlined />}
                      data-testid="approve-leave"
                      aria-label={t('leave.approveBtn')}
                    />
                  </Popconfirm>
                </Tooltip>
                <Tooltip title={t('leave.rejectBtn')}>
                  <Button
                    danger
                    size="small"
                    className="touch-button"
                    icon={<CloseOutlined />}
                    onClick={() => setRejectTarget(record)}
                    data-testid="reject-leave"
                    aria-label={t('leave.rejectBtn')}
                  />
                </Tooltip>
              </>
            )}
            {record.status !== 'REJECTED' && (
              <>
                <Tooltip title={t('common.edit')}>
                  <Button
                    size="small"
                    className="touch-button"
                    icon={<EditOutlined />}
                    onClick={() => onEdit && onEdit(record)}
                    data-testid="edit-leave"
                    aria-label={t('common.edit')}
                  />
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                  <Popconfirm
                    title={t('leave.deleteRequest')}
                    description={t('leave.deleteConfirm')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                  >
                    <Button
                      size="small"
                      className="touch-button"
                      icon={<DeleteOutlined />}
                      data-testid="delete-leave"
                      aria-label={t('common.delete')}
                    />
                  </Popconfirm>
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const renderActionButtons = (record) => {
    if (!canApprove) {
      if (record.status !== 'PENDING') return null;
      return (
        <Space wrap>
          <Tooltip title={t('common.edit')}>
            <Button size="small" className="touch-button" icon={<EditOutlined />} onClick={() => onEdit && onEdit(record)} data-testid="edit-leave" />
          </Tooltip>
          <Popconfirm title={t('leave.deleteRequest')} description={t('leave.deleteConfirm')} onConfirm={() => handleDelete(record.id)} okText={t('common.yes')} cancelText={t('common.no')}>
            <Button size="small" className="touch-button" icon={<DeleteOutlined />} data-testid="delete-leave" />
          </Popconfirm>
        </Space>
      );
    }
    return (
      <Space wrap>
        <Tooltip title={t('leave.downloadPdf')}>
          <Button size="small" className="touch-button" icon={<DownloadOutlined />} onClick={() => handleDownloadPdf(record.id, record.employeeName)} data-testid="download-pdf" />
        </Tooltip>
        {record.status === 'PENDING' && (
          <>
            <Tooltip title={t('leave.approveBtn')}>
              <Popconfirm title={t('leave.approveRequest')} description={t('leave.approveConfirm')} onConfirm={() => handleApproveReject(record.id, 'APPROVED')} okText={t('common.yes')} cancelText={t('common.no')}>
                <Button type="primary" size="small" className="touch-button" icon={<CheckOutlined />} data-testid="approve-leave" aria-label={t('leave.approveBtn')} />
              </Popconfirm>
            </Tooltip>
            <Tooltip title={t('leave.rejectBtn')}>
              <Button danger size="small" className="touch-button" icon={<CloseOutlined />} onClick={() => setRejectTarget(record)} data-testid="reject-leave" aria-label={t('leave.rejectBtn')} />
            </Tooltip>
          </>
        )}
        {record.status !== 'REJECTED' && (
          <>
            <Tooltip title={t('common.edit')}>
              <Button size="small" className="touch-button" icon={<EditOutlined />} onClick={() => onEdit && onEdit(record)} data-testid="edit-leave" aria-label={t('common.edit')} />
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <Popconfirm title={t('leave.deleteRequest')} description={t('leave.deleteConfirm')} onConfirm={() => handleDelete(record.id)} okText={t('common.yes')} cancelText={t('common.no')}>
                <Button size="small" className="touch-button" icon={<DeleteOutlined />} data-testid="delete-leave" aria-label={t('common.delete')} />
              </Popconfirm>
            </Tooltip>
          </>
        )}
      </Space>
    );
  };

  const renderCards = (items) =>
    items.map((record) => {
      const actions = renderActionButtons(record);
      const approvalMeta = renderApprovalMeta(record);
      return (
      <Card
        key={record.id}
        size="small"
        data-testid="leave-request"
        style={{ marginBottom: 12 }}
        actions={actions ? [actions] : undefined}
      >
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Space>
            <UserOutlined />
            <div>
              <Text strong>{record.employeeName}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{record.departmentName}</Text>
            </div>
          </Space>
          <Space wrap>
            <Tag color={getLeaveTypeColor(record.leaveType)}>{t(`leave.form.types.${record.leaveType}`)}</Tag>
            <Tag color={getStatusColor(record.status)}>{t(`leave.status.${record.status}`)}</Tag>
          </Space>
          {approvalMeta.length > 0 && (
            <Space direction="vertical" size={2} align="start">{approvalMeta}</Space>
          )}
          <Space>
            <CalendarOutlined />
            <Text>{formatDateRo(record.startDate)} – {formatDateRo(record.endDate)}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }} data-testid="working-days">
            {record.workingDays === 1 ? t('leave.daysCount_one', { count: 1 }) : t('leave.daysCount_other', { count: record.workingDays })}
          </Text>
          {record.reason && <Text type="secondary">{record.reason}</Text>}
        </Space>
      </Card>
    );
  });

  const processedRequests = [...requests]
    .sort((a, b) => {
      if (
        a.id !== null &&
        a.id !== undefined &&
        b.id !== null &&
        b.id !== undefined &&
        b.id !== a.id
      ) {
        return b.id - a.id;
      }

      return new Date(b.startDate) - new Date(a.startDate);
    });

  // Distinct employees among the loaded requests — the filter only needs people who actually
  // have requests, so we derive options from the data instead of a separate employees fetch.
  const employeeOptions = Array.from(
    new Map(
      requests
        .filter((r) => r.employeeId !== null && r.employeeId !== undefined)
        .map((r) => [String(r.employeeId), r.employeeName])
    ).entries()
  ).map(([value, label]) => ({ value, label }));

  const filteredRequests = processedRequests.filter((r) => {
    if (!canApprove) {
      return true;
    }
    const statusMatches = statusFilter === 'ALL' || r.status === statusFilter;
    const employeeMatches =
      employeeFilter === EMPLOYEE_FILTER_ALL || String(r.employeeId) === employeeFilter;
    return statusMatches && employeeMatches;
  });

  const showTable = view === 'table';

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        {canApprove && (
          <Space wrap>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 200 }}
              data-testid="status-filter"
              options={[
                { value: 'ALL', label: t('leave.filter.all') },
                { value: 'PENDING', label: t('leave.filter.pending') },
                { value: 'APPROVED', label: t('leave.filter.approved') },
                { value: 'REJECTED', label: t('leave.filter.rejected') },
              ]}
            />
            <Select
              value={employeeFilter}
              onChange={setEmployeeFilter}
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              data-testid="employee-filter"
              placeholder={t('leave.filter.employeePlaceholder')}
              options={[
                { value: EMPLOYEE_FILTER_ALL, label: t('leave.filter.allEmployees') },
                ...employeeOptions,
              ]}
            />
          </Space>
        )}
        <ViewModeSelect value={manualView} onChange={setManualView} data-testid="view-mode-select" />
      </div>

      <div ref={containerRef}>
        {showTable ? (
          <Table
            columns={columns}
            dataSource={filteredRequests}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              responsive: true,
              showTotal: (total) => t('leave.total', { total }),
            }}
            scroll={{ x: 'max-content' }}
            onRow={() => ({ 'data-testid': 'leave-request' })}
          />
        ) : (
          <div>
            {loading ? (
              <Card loading style={{ marginBottom: 12 }} />
            ) : (
              renderCards(filteredRequests)
            )}
            {filteredRequests.length > 0 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {t('leave.total', { total: filteredRequests.length })}
              </Text>
            )}
          </div>
        )}
      </div>

      <RejectionModal
        open={!!rejectTarget}
        onCancel={closeRejectModal}
        onConfirm={handleRejectSubmit}
      />
    </Card>
  );
};

export default LeaveRequestList;
